import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../supabase/auth";
import { supabase } from "../../../supabase/supabase";
import { useOrganization } from "../../App";
import { CallRequest } from "../../models/CallRequest";
import { callRequestService } from "../../services/callRequestService";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useToast } from "@/components/ui/use-toast";
import { Pencil, Phone, Plus, Search, Trash2, FileText } from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { format } from "date-fns";
import { NoData } from "@/components/ui/no-data";

export default function CallRequestsList() {
  const [callRequests, setCallRequests] = useState<CallRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [currentCallRequest, setCurrentCallRequest] =
    useState<CallRequest | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // Form data
  const [assistantId, setAssistantId] = useState<string | null>(null);
  const [assistantVariables, setAssistantVariables] = useState<
    Record<string, any>
  >({});
  const [customVariables, setCustomVariables] = useState<Record<string, any>>(
    {},
  );

  // Lists for dropdowns
  const [assistants, setAssistants] = useState<
    Array<{ id: string; name: string }>
  >([]);
  const [leads, setLeads] = useState<
    Array<{
      id: string;
      first_name: string;
      last_name: string;
      phone_number: string;
    }>
  >([]);
  const [phoneNumbers, setPhoneNumbers] = useState<
    Array<{ id: string; phone_number: string }>
  >([]);
  const [selectedLeadId, setSelectedLeadId] = useState<string | null>(null);
  const [selectedPhoneNumberId, setSelectedPhoneNumberId] = useState<
    string | null
  >(null);
  const [assistantVariableNames, setAssistantVariableNames] = useState<
    string[]
  >([]);

  // Custom variable management
  const [customVarKey, setCustomVarKey] = useState("");
  const [customVarValue, setCustomVarValue] = useState("");

  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const { currentOrganization } = useOrganization();

  useEffect(() => {
    if (currentOrganization) {
      fetchCallRequests();
      fetchAssistants();
      fetchLeads();
      fetchPhoneNumbers();

      // Set up realtime subscription
      const subscription = supabase
        .channel(`call-requests-changes-${currentOrganization.id}`)
        .on(
          "postgres_changes",
          {
            event: "*",
            schema: "public",
            table: "call_requests",
            filter: `organization_id=eq.${currentOrganization.id}`,
          },
          () => fetchCallRequests(),
        )
        .subscribe();

      return () => {
        subscription.unsubscribe();
      };
    }
  }, [currentOrganization]);

  // Fetch call requests
  const fetchCallRequests = async () => {
    if (!currentOrganization) return;

    try {
      setLoading(true);
      const data = await callRequestService.getCallRequests(
        currentOrganization.id,
      );
      setCallRequests(data);
    } catch (error) {
      console.error("Error fetching call requests:", error);
      toast({
        title: "Error",
        description: "Failed to fetch call requests. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // Fetch assistants for dropdown and auto-select the first one
  const fetchAssistants = async () => {
    if (!currentOrganization) return;

    try {
      const { data, error } = await supabase
        .from("assistants")
        .select("id, name")
        .eq("organization_id", currentOrganization.id)
        .eq("status", "published") // Only fetch published assistants
        .order("name");

      if (error) throw error;
      setAssistants(data || []);

      // Auto-select the first assistant if available and none is selected
      if (data && data.length > 0 && !assistantId) {
        setAssistantId(data[0].id);
        fetchAssistantVariables(data[0].id);
      }
    } catch (error) {
      console.error("Error fetching assistants:", error);
      toast({
        title: "Error",
        description: "Failed to load assistants. Please try again.",
        variant: "destructive",
      });
    }
  };

  // Fetch leads for dropdown
  const fetchLeads = async () => {
    if (!currentOrganization) return;

    try {
      const { data, error } = await supabase
        .from("leads")
        .select("id, first_name, last_name, phone_number")
        .eq("organization_id", currentOrganization.id)
        .order("first_name");

      if (error) throw error;
      setLeads(data || []);
    } catch (error) {
      console.error("Error fetching leads:", error);
      toast({
        title: "Error",
        description: "Failed to load leads. Please try again.",
        variant: "destructive",
      });
    }
  };

  // Fetch phone numbers for dropdown (only inbound numbers)
  const fetchPhoneNumbers = async () => {
    if (!currentOrganization) return;

    try {
      const { data, error } = await supabase
        .from("phone_numbers")
        .select("id, phone_number")
        .eq("organization_id", currentOrganization.id)
        .eq("type", "inbound")
        .order("phone_number");

      if (error) throw error;
      setPhoneNumbers(data || []);

      // Auto-select the first phone number if available
      if (data && data.length > 0 && !selectedPhoneNumberId) {
        setSelectedPhoneNumberId(data[0].id);
      }
    } catch (error) {
      console.error("Error fetching phone numbers:", error);
      toast({
        title: "Error",
        description: "Failed to load phone numbers. Please try again.",
        variant: "destructive",
      });
    }
  };

  // Fetch assistant variables when assistant is selected
  const fetchAssistantVariables = async (selectedAssistantId: string) => {
    try {
      const variables =
        await callRequestService.getAssistantVariables(selectedAssistantId);
      setAssistantVariableNames(variables);

      // Initialize assistant variables with empty values
      const initialVariables: Record<string, any> = {};
      variables.forEach((variable) => {
        initialVariables[variable] = "";
      });
      setAssistantVariables(initialVariables);
    } catch (error) {
      console.error("Error fetching assistant variables:", error);
      toast({
        title: "Warning",
        description:
          "Could not load assistant variables. You can still proceed with the request.",
        variant: "default",
      });
    }
  };

  // Handle assistant selection
  const handleAssistantChange = (selectedAssistantId: string) => {
    setAssistantId(selectedAssistantId);
    if (selectedAssistantId) {
      fetchAssistantVariables(selectedAssistantId);
    } else {
      setAssistantVariableNames([]);
      setAssistantVariables({});
    }
  };

  // Handle assistant variable change
  const handleAssistantVariableChange = (variable: string, value: string) => {
    setAssistantVariables((prev) => ({
      ...prev,
      [variable]: value,
    }));
  };

  // Add custom variable
  const handleAddCustomVariable = () => {
    if (!customVarKey.trim()) {
      toast({
        title: "Error",
        description: "Variable name cannot be empty",
        variant: "destructive",
      });
      return;
    }

    setCustomVariables((prev) => ({
      ...prev,
      [customVarKey]: customVarValue,
    }));
    setCustomVarKey("");
    setCustomVarValue("");
  };

  // Remove custom variable
  const handleRemoveCustomVariable = (key: string) => {
    const newVars = { ...customVariables };
    delete newVars[key];
    setCustomVariables(newVars);
  };

  // Reset form
  const resetForm = () => {
    setSelectedLeadId(null);
    setSelectedPhoneNumberId(null);
    setAssistantId(null);
    setAssistantVariables({});
    setCustomVariables({});
    setAssistantVariableNames([]);
    setCustomVarKey("");
    setCustomVarValue("");
    setCurrentCallRequest(null);
  };

  // Open create dialog
  const openCreateDialog = () => {
    resetForm();
    setIsCreateDialogOpen(true);
  };

  // Open edit dialog
  const openEditDialog = (callRequest: CallRequest) => {
    setCurrentCallRequest(callRequest);
    setAssistantId(callRequest.assistant_id);
    setSelectedLeadId(callRequest.lead_id || null);
    setSelectedPhoneNumberId(callRequest.phone_number_id || null);
    setAssistantVariables(callRequest.assistant_variables || {});
    setCustomVariables(callRequest.custom_variables || {});

    if (callRequest.assistant_id) {
      fetchAssistantVariables(callRequest.assistant_id);
    }

    setIsEditDialogOpen(true);
  };

  // Open delete dialog
  const openDeleteDialog = (callRequest: CallRequest) => {
    setCurrentCallRequest(callRequest);
    setIsDeleteDialogOpen(true);
  };

  // Open view dialog
  const openViewDialog = (callRequest: CallRequest) => {
    setCurrentCallRequest(callRequest);
    setIsViewDialogOpen(true);
  };

  // Create call request
  const handleCreateCallRequest = async () => {
    if (!validateForm()) return;
    if (!currentOrganization || !user) return;

    const selectedLead = leads.find((lead) => lead.id === selectedLeadId);
    if (!selectedLead) {
      toast({
        title: "Error",
        description: "Selected lead not found.",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsSaving(true);
      await callRequestService.createCallRequest(
        {
          assistant_id: assistantId,
          lead_id: selectedLeadId,
          phone_number_id: selectedPhoneNumberId,
          assistant_variables: assistantVariables,
          custom_variables: {
            ...customVariables,
            lead_first_name: selectedLead.first_name,
            lead_last_name: selectedLead.last_name,
            lead_phone_number: selectedLead.phone_number,
          },
        },
        currentOrganization.id,
        user.id,
      );

      toast({
        title: "Success",
        description: "Call request created successfully.",
      });

      setIsCreateDialogOpen(false);
      resetForm();
      fetchCallRequests();
    } catch (error) {
      console.error("Error creating call request:", error);
      toast({
        title: "Error",
        description: "Failed to create call request. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Update call request
  const handleUpdateCallRequest = async () => {
    if (!validateForm()) return;
    if (!currentCallRequest) return;

    const selectedLead = leads.find((lead) => lead.id === selectedLeadId);
    if (!selectedLead) {
      toast({
        title: "Error",
        description: "Selected lead not found.",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsSaving(true);
      await callRequestService.updateCallRequest(currentCallRequest.id, {
        assistant_id: assistantId,
        lead_id: selectedLeadId,
        phone_number_id: selectedPhoneNumberId,
        assistant_variables: assistantVariables,
        custom_variables: {
          ...customVariables,
          lead_first_name: selectedLead.first_name,
          lead_last_name: selectedLead.last_name,
          lead_phone_number: selectedLead.phone_number,
        },
      });

      toast({
        title: "Success",
        description: "Call request updated successfully.",
      });

      setIsEditDialogOpen(false);
      resetForm();
      fetchCallRequests();
    } catch (error) {
      console.error("Error updating call request:", error);
      toast({
        title: "Error",
        description: "Failed to update call request. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Delete call request
  const handleDeleteCallRequest = async () => {
    if (!currentCallRequest) return;

    try {
      await callRequestService.deleteCallRequest(currentCallRequest.id);

      toast({
        title: "Success",
        description: "Call request deleted successfully.",
      });

      setIsDeleteDialogOpen(false);
      fetchCallRequests();
    } catch (error) {
      console.error("Error deleting call request:", error);
      toast({
        title: "Error",
        description: "Failed to delete call request. Please try again.",
        variant: "destructive",
      });
    }
  };

  // Validate form
  const validateForm = () => {
    if (!selectedLeadId) {
      toast({
        title: "Missing Information",
        description: "Lead selection is required.",
        variant: "destructive",
      });
      return false;
    }

    if (!assistantId) {
      toast({
        title: "Missing Information",
        description: "Assistant selection is required.",
        variant: "destructive",
      });
      return false;
    }

    if (!selectedPhoneNumberId) {
      toast({
        title: "Missing Information",
        description: "Inbound phone number selection is required.",
        variant: "destructive",
      });
      return false;
    }

    return true;
  };

  // Filter call requests based on search query
  const filteredCallRequests = callRequests.filter((request) => {
    const searchLower = searchQuery.toLowerCase();
    const lead = leads.find((l) => l.id === request.lead_id);

    return (
      request.id.toLowerCase().includes(searchLower) ||
      lead?.phone_number.toLowerCase().includes(searchLower) ||
      lead?.first_name.toLowerCase().includes(searchLower) ||
      lead?.last_name.toLowerCase().includes(searchLower) ||
      assistants
        .find((a) => a.id === request.assistant_id)
        ?.name.toLowerCase()
        .includes(searchLower)
    );
  });

  return (
    <div className="container mx-auto py-6 bg-background text-foreground">
      <div className="bg-card rounded-lg shadow-sm border border-border p-6">
        <div className="flex justify-end items-center mb-6">
          <Button
            variant="primaryOutline"
            onClick={openCreateDialog}
            className="flex items-center gap-2"
          >
            <Plus size={16} />
            New Call Request
          </Button>
        </div>

        {!loading && callRequests.length > 0 && (
          <div className="flex items-center mb-6 relative">
            <Search className="absolute left-3 text-muted-foreground" size={18} />
            <Input
              placeholder="Search by lead name, phone number, or assistant..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 w-full max-w-md"
            />
          </div>
        )}

        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : filteredCallRequests.length === 0 ? (
          <NoData
            icon={<Phone className="h-12 w-12 text-muted-foreground/50" />}
            title={
              searchQuery
                ? "No call requests match your search"
                : "No call requests found"
            }
            description={
              searchQuery
                ? "Try adjusting your search terms or clear the search to see all call requests."
                : "Get started by creating your first call request."
            }
            actionButton={
              !searchQuery && (
                <Button
                  variant="primaryOutline"
                  onClick={openCreateDialog}
                  className="mt-2"
                >
                  <Plus size={16} className="mr-2" />
                  Create Your First Call Request
                </Button>
              )
            }
          />
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Assistant</TableHead>
                  <TableHead>Lead Phone Number</TableHead>
                  <TableHead>Inbound Phone Number</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Scheduled Time</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredCallRequests.map((request) => (
                  <TableRow key={request.id}>
                    <TableCell>
                      {assistants.find((a) => a.id === request.assistant_id)
                        ?.name || "-"}
                    </TableCell>
                    <TableCell>
                      {leads.find((l) => l.id === request.lead_id)
                        ?.phone_number ? (
                        <a
                          href={`tel:${leads.find((l) => l.id === request.lead_id)?.phone_number}`}
                          className="text-blue-600 hover:text-blue-800 hover:underline"
                        >
                          {
                            leads.find((l) => l.id === request.lead_id)
                              ?.phone_number
                          }
                        </a>
                      ) : (
                        "-"
                      )}
                    </TableCell>
                    <TableCell>
                      {phoneNumbers.find(
                        (p) => p.id === request.phone_number_id,
                      )?.phone_number ? (
                        <a
                          href={`tel:${phoneNumbers.find((p) => p.id === request.phone_number_id)?.phone_number}`}
                          className="text-blue-600 hover:text-blue-800 hover:underline"
                        >
                          {
                            phoneNumbers.find(
                              (p) => p.id === request.phone_number_id,
                            )?.phone_number
                          }
                        </a>
                      ) : (
                        "-"
                      )}
                    </TableCell>
                    <TableCell>
                      <span
                        className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${
                          request.status === "pending"
                            ? "bg-yellow-100 text-yellow-800"
                            : request.status === "completed"
                              ? "bg-green-100 text-green-800"
                              : request.status === "failed"
                                ? "bg-red-100 text-red-800"
                                : request.status === "scheduled"
                                  ? "bg-blue-100 text-blue-800"
                                  : "bg-gray-100 text-gray-800"
                        }`}
                      >
                        {request.status}
                      </span>
                    </TableCell>
                    <TableCell>
                      {new Date(request.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <span className="text-gray-400">N/A</span>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => openViewDialog(request)}
                          title="View Details"
                        >
                          <FileText size={16} className="text-blue-600" />
                        </Button>
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => openEditDialog(request)}
                          title="Edit"
                        >
                          <Pencil size={16} className="text-blue-600" />
                        </Button>
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => openDeleteDialog(request)}
                          title="Delete"
                        >
                          <Trash2 size={16} className="text-red-600" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      {/* Create Call Request Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>Create New Call Request</DialogTitle>
          </DialogHeader>
          <ScrollArea className="flex-1 pr-4">
            <div className="grid gap-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="lead">Lead *</Label>
                <Select
                  value={selectedLeadId || ""}
                  onValueChange={(value) => setSelectedLeadId(value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a lead" />
                  </SelectTrigger>
                  <SelectContent>
                    {leads.map((lead) => (
                      <SelectItem key={lead.id} value={lead.id}>
                        {lead.first_name} {lead.last_name} - {lead.phone_number}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {leads.length === 0 && (
                  <p className="text-xs text-muted-foreground mt-1">
                    No leads available. Please create a lead first.
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="assistant">Assistant *</Label>
                <Select
                  value={assistantId || ""}
                  onValueChange={handleAssistantChange}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select an assistant" />
                  </SelectTrigger>
                  <SelectContent>
                    {assistants.map((assistant) => (
                      <SelectItem key={assistant.id} value={assistant.id}>
                        {assistant.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {assistants.length === 0 && (
                  <p className="text-xs text-muted-foreground mt-1">
                    No published assistants available. Please create and publish
                    an assistant first.
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone_number">Inbound Phone Number *</Label>
                <Select
                  value={selectedPhoneNumberId || ""}
                  onValueChange={(value) => setSelectedPhoneNumberId(value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select an inbound phone number" />
                  </SelectTrigger>
                  <SelectContent>
                    {phoneNumbers.map((phoneNumber) => (
                      <SelectItem key={phoneNumber.id} value={phoneNumber.id}>
                        {phoneNumber.phone_number}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {phoneNumbers.length === 0 && (
                  <p className="text-xs text-muted-foreground mt-1">
                    No inbound phone numbers available. Please add a phone
                    number in the Phone Numbers settings.
                  </p>
                )}
              </div>

              {/* Assistant Variables */}
              {assistantVariableNames.length > 0 && (
                <div className="space-y-4 mt-4">
                  <h3 className="text-lg font-medium">Assistant Variables</h3>
                  <div className="grid gap-4">
                    {assistantVariableNames.map((variable) => (
                      <div key={variable} className="space-y-2">
                        <Label htmlFor={`var-${variable}`}>{variable}</Label>
                        <Input
                          id={`var-${variable}`}
                          value={assistantVariables[variable] || ""}
                          onChange={(e) =>
                            handleAssistantVariableChange(
                              variable,
                              e.target.value,
                            )
                          }
                          placeholder={`Value for ${variable}`}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Custom Variables */}
              <div className="space-y-4 mt-4">
                <h3 className="text-lg font-medium">Custom Variables</h3>
                <div className="grid grid-cols-[1fr_2fr_auto] gap-2 items-end">
                  <div>
                    <Label htmlFor="customVarKey">Variable Name</Label>
                    <Input
                      id="customVarKey"
                      value={customVarKey}
                      onChange={(e) => setCustomVarKey(e.target.value)}
                      placeholder="Name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="customVarValue">Value</Label>
                    <Input
                      id="customVarValue"
                      value={customVarValue}
                      onChange={(e) => setCustomVarValue(e.target.value)}
                      placeholder="Value"
                    />
                  </div>
                  <Button
                    type="button"
                    onClick={handleAddCustomVariable}
                    disabled={!customVarKey.trim()}
                  >
                    Add
                  </Button>
                </div>

                {/* Display custom variables */}
                {Object.keys(customVariables).length > 0 && (
                  <div className="mt-4 border rounded-md p-4">
                    <h4 className="text-sm font-medium mb-2">
                      Added Custom Variables:
                    </h4>
                    <div className="space-y-2">
                      {Object.entries(customVariables).map(([key, value]) => (
                        <div
                          key={key}
                          className="flex justify-between items-center bg-muted/50 p-2 rounded"
                        >
                          <div>
                            <span className="font-medium">{key}:</span> {value}
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemoveCustomVariable(key)}
                          >
                            <Trash2 size={14} className="text-red-500" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </ScrollArea>
          <DialogFooter className="mt-6">
            <Button
              variant="outline"
              onClick={() => setIsCreateDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button onClick={handleCreateCallRequest} disabled={isSaving}>
              {isSaving ? "Creating..." : "Create Call Request"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Call Request Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>Edit Call Request</DialogTitle>
          </DialogHeader>
          <ScrollArea className="flex-1 pr-4">
            <div className="grid gap-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="lead">Lead *</Label>
                <Select
                  value={selectedLeadId || ""}
                  onValueChange={(value) => setSelectedLeadId(value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a lead" />
                  </SelectTrigger>
                  <SelectContent>
                    {leads.map((lead) => (
                      <SelectItem key={lead.id} value={lead.id}>
                        {lead.first_name} {lead.last_name} - {lead.phone_number}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {leads.length === 0 && (
                  <p className="text-xs text-muted-foreground mt-1">
                    No leads available. Please create a lead first.
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="assistant">Assistant *</Label>
                <Select
                  value={assistantId || ""}
                  onValueChange={handleAssistantChange}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select an assistant" />
                  </SelectTrigger>
                  <SelectContent>
                    {assistants.map((assistant) => (
                      <SelectItem key={assistant.id} value={assistant.id}>
                        {assistant.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {assistants.length === 0 && (
                  <p className="text-xs text-muted-foreground mt-1">
                    No published assistants available. Please create and publish
                    an assistant first.
                  </p>
                )}
              </div>

              {/* Assistant Variables */}
              {assistantVariableNames.length > 0 && (
                <div className="space-y-4 mt-4">
                  <h3 className="text-lg font-medium">Assistant Variables</h3>
                  <div className="grid gap-4">
                    {assistantVariableNames.map((variable) => (
                      <div key={variable} className="space-y-2">
                        <Label htmlFor={`var-${variable}`}>{variable}</Label>
                        <Input
                          id={`var-${variable}`}
                          value={assistantVariables[variable] || ""}
                          onChange={(e) =>
                            handleAssistantVariableChange(
                              variable,
                              e.target.value,
                            )
                          }
                          placeholder={`Value for ${variable}`}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Custom Variables */}
              <div className="space-y-4 mt-4">
                <h3 className="text-lg font-medium">Custom Variables</h3>
                <div className="grid grid-cols-[1fr_2fr_auto] gap-2 items-end">
                  <div>
                    <Label htmlFor="customVarKey">Variable Name</Label>
                    <Input
                      id="customVarKey"
                      value={customVarKey}
                      onChange={(e) => setCustomVarKey(e.target.value)}
                      placeholder="Name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="customVarValue">Value</Label>
                    <Input
                      id="customVarValue"
                      value={customVarValue}
                      onChange={(e) => setCustomVarValue(e.target.value)}
                      placeholder="Value"
                    />
                  </div>
                  <Button
                    type="button"
                    onClick={handleAddCustomVariable}
                    disabled={!customVarKey.trim()}
                  >
                    Add
                  </Button>
                </div>

                {/* Display custom variables */}
                {Object.keys(customVariables).length > 0 && (
                  <div className="mt-4 border rounded-md p-4">
                    <h4 className="text-sm font-medium mb-2">
                      Added Custom Variables:
                    </h4>
                    <div className="space-y-2">
                      {Object.entries(customVariables).map(([key, value]) => (
                        <div
                          key={key}
                          className="flex justify-between items-center bg-muted/50 p-2 rounded"
                        >
                          <div>
                            <span className="font-medium">{key}:</span> {value}
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemoveCustomVariable(key)}
                          >
                            <Trash2 size={14} className="text-red-500" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </ScrollArea>
          <DialogFooter className="mt-6">
            <Button
              variant="outline"
              onClick={() => setIsEditDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button onClick={handleUpdateCallRequest} disabled={isSaving}>
              {isSaving ? "Updating..." : "Update Call Request"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Call Request Dialog */}
      <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>Call Request Details</DialogTitle>
          </DialogHeader>
          <ScrollArea className="flex-1 pr-4">
            {currentCallRequest && (
              <div className="space-y-6 py-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <h3 className="text-sm font-medium text-muted-foreground">
                      ID
                    </h3>
                    <p className="text-sm">{currentCallRequest.id}</p>
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-muted-foreground">
                      Status
                    </h3>
                    <p className="text-sm">{currentCallRequest.status}</p>
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-muted-foreground">
                      Created
                    </h3>
                    <p className="text-sm">
                      {new Date(currentCallRequest.created_at).toLocaleString()}
                    </p>
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-muted-foreground">
                      Updated
                    </h3>
                    <p className="text-sm">
                      {new Date(currentCallRequest.updated_at).toLocaleString()}
                    </p>
                  </div>
                </div>

                <div className="space-y-2">
                  <h3 className="text-sm font-medium text-muted-foreground">
                    Lead
                  </h3>
                  <p className="text-sm">
                    {(() => {
                      const lead = leads.find(
                        (l) => l.id === currentCallRequest.lead_id,
                      );
                      return lead
                        ? `${lead.first_name} ${lead.last_name}`
                        : "N/A";
                    })()}
                  </p>
                </div>

                <div className="space-y-2">
                  <h3 className="text-sm font-medium text-muted-foreground">
                    Assistant
                  </h3>
                  <p className="text-sm">
                    {assistants.find(
                      (a) => a.id === currentCallRequest.assistant_id,
                    )?.name || "Not specified"}
                  </p>
                </div>

                <div className="space-y-2">
                  <h3 className="text-sm font-medium text-muted-foreground">
                    Lead Phone Number
                  </h3>
                  <p className="text-sm">
                    {leads.find((l) => l.id === currentCallRequest.lead_id)
                      ?.phone_number ? (
                      <a
                        href={`tel:${leads.find((l) => l.id === currentCallRequest.lead_id)?.phone_number}`}
                        className="text-blue-600 hover:text-blue-800 hover:underline"
                      >
                        {
                          leads.find((l) => l.id === currentCallRequest.lead_id)
                            ?.phone_number
                        }
                      </a>
                    ) : (
                      "N/A"
                    )}
                  </p>
                </div>

                <div className="space-y-2">
                  <h3 className="text-sm font-medium text-muted-foreground">
                    Inbound Phone Number
                  </h3>
                  <p className="text-sm">
                    {phoneNumbers.find(
                      (p) => p.id === currentCallRequest.phone_number_id,
                    )?.phone_number ? (
                      <a
                        href={`tel:${phoneNumbers.find((p) => p.id === currentCallRequest.phone_number_id)?.phone_number}`}
                        className="text-blue-600 hover:text-blue-800 hover:underline"
                      >
                        {
                          phoneNumbers.find(
                            (p) => p.id === currentCallRequest.phone_number_id,
                          )?.phone_number
                        }
                      </a>
                    ) : (
                      "N/A"
                    )}
                  </p>
                </div>

                {Object.keys(currentCallRequest.assistant_variables || {})
                  .length > 0 && (
                  <div className="space-y-2">
                    <h3 className="text-sm font-medium text-muted-foreground">
                      Assistant Variables
                    </h3>
                    <div className="bg-muted/30 p-4 rounded-md">
                      <pre className="text-xs overflow-auto">
                        {JSON.stringify(
                          currentCallRequest.assistant_variables,
                          null,
                          2,
                        )}
                      </pre>
                    </div>
                  </div>
                )}

                {Object.keys(currentCallRequest.custom_variables || {}).length >
                  0 && (
                  <div className="space-y-2">
                    <h3 className="text-sm font-medium text-muted-foreground">
                      Custom Variables
                    </h3>
                    <div className="bg-muted/30 p-4 rounded-md">
                      <pre className="text-xs overflow-auto">
                        {JSON.stringify(
                          currentCallRequest.custom_variables,
                          null,
                          2,
                        )}
                      </pre>
                    </div>
                  </div>
                )}
              </div>
            )}
          </ScrollArea>
          <DialogFooter className="mt-6">
            <Button
              variant="outline"
              onClick={() => setIsViewDialogOpen(false)}
            >
              Close
            </Button>
            <Button
              variant="default"
              onClick={() => {
                setIsViewDialogOpen(false);
                if (currentCallRequest) {
                  openEditDialog(currentCallRequest);
                }
              }}
            >
              Edit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this call request. This action cannot
              be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteCallRequest}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
